import { useEffect, useState } from 'react';
import { KeyRound, Loader2, Mail, AlertTriangle } from 'lucide-react';

import { api, ApiError } from '../lib/api.js';

interface CodeSent {
  sentTo: string;
  expiresInMinutes: number;
  emailSent: boolean;
  emailError: string | null;
}

/**
 * Sign in: password, then the code emailed to the admin's address.
 *
 * Two steps in one component because the first finishes nothing — a correct
 * password produces a session that can reach the code routes and nothing else.
 * Handing off to a separate screen between them would imply progress that has
 * not been made.
 */
export function Login({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [stage, setStage] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState<CodeSent | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  // A visible countdown, so nobody hammers Resend into its own rate limit
  // while waiting for an email that is simply in transit.
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<CodeSent>('/api/admin/login', { email, password });
      setPassword('');
      setSent(result);
      setStage('code');
      setResendIn(30);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/admin/code/verify', { code });
      setCode('');
      await onSignedIn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not verify that code.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<CodeSent>('/api/admin/code/resend');
      setSent(result);
      setNotice(result.emailSent ? 'A new code is on its way.' : null);
      if (!result.emailSent) setError(`The email could not be sent: ${result.emailError}`);
      setResendIn(30);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send another code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-gold-700">Gold Star Printing</h1>
          <p className="mt-1 text-sm text-slate-600">Payment portal — staff sign in</p>
        </div>

        <div className="card p-6">
          {stage === 'password' ? (
            <form onSubmit={submitPassword} className="space-y-4">
              <div className="flex items-center gap-2 text-slate-900">
                <KeyRound className="h-5 w-5 text-gold-600" aria-hidden />
                <h2 className="font-semibold">Sign in</h2>
              </div>
              <div>
                <label className="field-label" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="field-input"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="field-label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="field-input"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && (
                <p className="field-error" role="alert">
                  {error}
                </p>
              )}
              <button type="submit" className="btn-primary w-full" disabled={busy}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                Sign in
              </button>
              <p className="text-center text-xs text-slate-500">
                We will email you a six-digit code to finish signing in.
              </p>
            </form>
          ) : (
            <form onSubmit={submitCode} className="space-y-4">
              <div className="flex items-center gap-2 text-slate-900">
                <Mail className="h-5 w-5 text-gold-600" aria-hidden />
                <h2 className="font-semibold">Check your email</h2>
              </div>

              <p className="text-sm text-slate-600">
                We sent a six-digit code to <strong>{sent?.sentTo}</strong>. It expires in{' '}
                {sent?.expiresInMinutes ?? 10} minutes.
              </p>

              {sent && !sent.emailSent && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <div>
                    The email could not be sent{sent.emailError ? `: ${sent.emailError}` : '.'} You
                    will not receive a code until that is fixed.
                  </div>
                </div>
              )}

              <div>
                <label className="field-label" htmlFor="code">
                  Code
                </label>
                <input
                  id="code"
                  className="field-input font-account text-center text-lg tracking-[0.3em]"
                  inputMode="numeric"
                  // Lets iOS and Android offer the code straight from the
                  // notification, which is most of the convenience of doing
                  // this by email at all.
                  autoComplete="one-time-code"
                  maxLength={7}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  required
                />
              </div>

              {error && (
                <p className="field-error" role="alert">
                  {error}
                </p>
              )}
              {notice && <p className="text-sm text-emerald-700">{notice}</p>}

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={busy || code.replace(/\s/g, '').length < 6}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                Verify
              </button>

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-800 disabled:opacity-50"
                  onClick={() => void resend()}
                  disabled={busy || resendIn > 0}
                >
                  {resendIn > 0 ? `Send another code (${resendIn}s)` : 'Send another code'}
                </button>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-800"
                  onClick={() => {
                    setStage('password');
                    setCode('');
                    setError(null);
                    setNotice(null);
                  }}
                >
                  Start over
                </button>
              </div>

              <p className="border-t border-slate-100 pt-3 text-xs text-slate-500">
                Not in your inbox? Check spam. Gold Star Printing will never ask you for this code
                by phone or email.
              </p>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Accounts are created by an existing admin with{' '}
          <code className="font-account">npm run admin:invite</code>.
        </p>
      </div>
    </div>
  );
}
